import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { get } from "../../../utils/apiUtils";

/* ---- Types ---- */
type DocCountApi = { countInternal: number; countExternal: number };

export type CodeCountItem = {
  document_id: string;
  document_name: string;
  count_code: number;
};

export type HighlightCountItem = {
  document_id: string;
  document_name: string;
  count_highlights: number;
};

/* Codes × Descriptors */
export type CodesDescriptorItem = {
  code_id: string;
  code_name: string;
  descriptors: { descriptor_value: string; total_count: number }[];
};

/* /codes (tree -> flat) */
type CodeTree = { id: string; name: string; children?: CodeTree[] };
export type FlatCode = { id: string; name: string };

/* Shape i grupuar i /descriptor-codes-count */
export type DescriptorCodesGroup = {
  Set: string;
  Field: string;
  descriptor_value: { descriptor_value: string; total_count: number }[];
};

/* === NEW: Documents dropdown + density + richness === */
export type SimpleDoc = { id: string; name: string };

export type CodingRichnessDoc = {
  document_id: string;
  document_name: string;
  exclusive_codes_count: number;
  shared_codes_count: number;
};
export type CodingRichnessApi = {
  project_id: string;
  documents: CodingRichnessDoc[];
};

export type CodeDensityApi = {
  document_id: string;
  document_name: string;
  total_code_applications: number;
  word_count: number;
  code_density_per_1000_words: number;
};

/* ✅ NEW: Document coverage */
export type DocumentCoverageApi = {
  document_id: string;
  word_count: number;
  highlighted_words: number;
  coverage_percent: number;
};

/* ✅ NEW: Highlight distribution */
export type HighlightDistributionBin = { length: number; count: number };
export type HighlightDistributionApi = {
  project_id: string;
  distribution: HighlightDistributionBin[];
};

type AnalyzeState = {
  projectId: string | null;
  month?: number;
  year?: number;

  counts: {
    internal: number;
    external: number;
    loading: boolean;
    error: string | null;
  };

  codes: {
    rows: CodeCountItem[];
    loading: boolean;
    error: string | null;
    max: number;
  };

  highlights: {
    rows: HighlightCountItem[];
    loading: boolean;
    error: string | null;
    max: number;
  };

  codesDescriptor: {
    items: CodesDescriptorItem[];
    loading: boolean;
    error: string | null;
  };

  codesDropdown: {
    items: FlatCode[];
    loading: boolean;
    error: string | null;
  };

  descriptorCodes: {
    rows: { descriptor_value: string; total_count: number }[];
    max: number;
    groups: DescriptorCodesGroup[];
    loading: boolean;
    error: string | null;
  };

  documentsDropdown: {
    items: SimpleDoc[];
    loading: boolean;
    error: string | null;
  };

  codeDensity: {
    document_id: string;
    document_name: string;
    total_code_applications: number;
    word_count: number;
    code_density_per_1000_words: number;
    loading: boolean;
    error: string | null;
  };

  codingRichness: {
    rows: CodingRichnessDoc[];
    loading: boolean;
    error: string | null;
    maxExclusive: number;
    maxShared: number;
  };

  documentCoverage: {
    document_id: string;
    word_count: number;
    highlighted_words: number;
    coverage_percent: number;
    loading: boolean;
    error: string | null;
  };

  highlightDistribution: {
    rows: HighlightDistributionBin[];
    loading: boolean;
    error: string | null;
  };
};

const initialState: AnalyzeState = {
  projectId: null,
  month: undefined,
  year: undefined,

  counts: { internal: 0, external: 0, loading: false, error: null },
  codes: { rows: [], loading: false, error: null, max: 1 },
  highlights: { rows: [], loading: false, error: null, max: 1 },

  codesDescriptor: { items: [], loading: false, error: null },

  codesDropdown: { items: [], loading: false, error: null },

  descriptorCodes: {
    rows: [],
    max: 1,
    groups: [],
    loading: false,
    error: null,
  },

  documentsDropdown: { items: [], loading: false, error: null },

  codeDensity: {
    document_id: "",
    document_name: "",
    total_code_applications: 0,
    word_count: 0,
    code_density_per_1000_words: 0,
    loading: false,
    error: null,
  },

  codingRichness: {
    rows: [],
    loading: false,
    error: null,
    maxExclusive: 1,
    maxShared: 1,
  },

  documentCoverage: {
    document_id: "",
    word_count: 0,
    highlighted_words: 0,
    coverage_percent: 0,
    loading: false,
    error: null,
  },

  highlightDistribution: {
    rows: [],
    loading: false,
    error: null,
  },
};

/* ---- Thunks ---- */
export const fetchDocCounts = createAsyncThunk(
  "analyze/fetchDocCounts",
  async (_: void, { getState }) => {
    const s = (getState() as any).analyze as AnalyzeState;
    if (!s.projectId) throw new Error("Missing project id");

    const res = await get<DocCountApi>(
      `documents/count/?project_id=${s.projectId}` +
        (s.month ? `&month=${s.month}` : "") +
        (s.year ? `&year=${s.year}` : ""),
      { headers: { Accept: "application/json" } }
    );

    if (res.type !== "success") throw new Error(res.data?.message || "Failed");
    return res.data;
  }
);

export const fetchCodeCounts = createAsyncThunk(
  "analyze/fetchCodeCounts",
  async (_: void, { getState }) => {
    const s = (getState() as any).analyze as AnalyzeState;
    if (!s.projectId) throw new Error("Missing project id");

    const res = await get<CodeCountItem[]>(
      `documents/code-count?project_id=${s.projectId}`,
      { headers: { Accept: "application/json" } }
    );

    if (res.type !== "success") throw new Error(res.data?.message || "Failed");

    const rows = Array.isArray(res.data) ? res.data : [];
    const max = rows.reduce((m, r) => Math.max(m, r.count_code || 0), 0) || 1;
    return { rows, max };
  }
);

export const fetchHighlightCounts = createAsyncThunk(
  "analyze/fetchHighlightCounts",
  async (_: void, { getState }) => {
    const s = (getState() as any).analyze as AnalyzeState;
    if (!s.projectId) throw new Error("Missing project id");

    const res = await get<HighlightCountItem[]>(
      `documents/highlight-count?project_id=${s.projectId}`,
      { headers: { Accept: "application/json" } }
    );

    if (res.type !== "success") throw new Error(res.data?.message || "Failed");

    const rows = Array.isArray(res.data) ? res.data : [];
    const max =
      rows.reduce((m, r) => Math.max(m, r.count_highlights || 0), 0) || 1;
    return { rows, max };
  }
);

export const fetchCodesDescriptorCount = createAsyncThunk(
  "analyze/fetchCodesDescriptorCount",
  async ({ set_field_id }: { set_field_id: string }, { getState }) => {
    const s = (getState() as any).analyze as AnalyzeState;
    if (!s.projectId) throw new Error("Missing project id");

    const res = await get<CodesDescriptorItem[]>(
      `codes-descriptor-count?set_field_id=${encodeURIComponent(set_field_id)}`,
      { headers: { Accept: "application/json" } }
    );

    if (res.type !== "success") throw new Error(res.data?.message || "Failed");
    return Array.isArray(res.data) ? res.data : [];
  }
);

export const fetchCodesFlat = createAsyncThunk(
  "analyze/fetchCodesFlat",
  async (_: void, { getState }) => {
    const s = (getState() as any).analyze as AnalyzeState;
    if (!s.projectId) throw new Error("Missing project id");

    const res = await get<{ items: CodeTree[]; total: number }>(
      `codes?project_id=${encodeURIComponent(s.projectId)}&skip=0&limit=1000`,
      { headers: { Accept: "application/json" } }
    );

    if (res.type !== "success") throw new Error(res.data?.message || "Failed");

    const items = Array.isArray(res.data?.items) ? res.data.items : [];
    const flat: FlatCode[] = [];

    const walk = (n: CodeTree) => {
      flat.push({ id: n.id, name: n.name });
      (n.children || []).forEach(walk);
    };
    items.forEach(walk);

    return flat;
  }
);

export const fetchDescriptorCodesCount = createAsyncThunk(
  "analyze/fetchDescriptorCodesCount",
  async ({ code_id }: { code_id: string }) => {
    const res = await get<
      | DescriptorCodesGroup[]
      | { descriptor_value: string; total_count: number }[]
    >(`descriptor-codes-count?code_id=${encodeURIComponent(code_id)}`, {
      headers: { Accept: "application/json" },
    });

    if (res.type !== "success") throw new Error(res.data?.message || "Failed");

    const data: any = res.data || [];

    if (
      Array.isArray(data) &&
      data[0] &&
      Array.isArray(data[0].descriptor_value)
    ) {
      return { groups: data as DescriptorCodesGroup[], rows: [], max: 1 };
    } else {
      const rows = (Array.isArray(data) ? data : []) as {
        descriptor_value: string;
        total_count: number;
      }[];

      const max =
        rows.reduce((m, r) => Math.max(m, Number(r.total_count) || 0), 0) || 1;
      return { groups: [], rows, max };
    }
  }
);

export const fetchDocumentsDropdown = createAsyncThunk(
  "analyze/fetchDocumentsDropdown",
  async (_: void, { getState }) => {
    const s = (getState() as any).analyze as AnalyzeState;
    if (!s.projectId) throw new Error("Missing project id");

    const res = await get<any>(
      `documents?project_id=${encodeURIComponent(
        s.projectId
      )}&skip=0&limit=1000`,
      { headers: { Accept: "application/json" } }
    );

    if (res.type !== "success") throw new Error(res.data?.message || "Failed");

    const items = Array.isArray(res.data?.items) ? res.data.items : [];
    const mapped: SimpleDoc[] = items
      .map((d: any) => ({
        id: d.id || d.document_id,
        name: d.document_name || d.title || d.name || "Document",
      }))
      .filter((x: any) => x.id);

    return mapped;
  }
);

export const fetchCodeDensity = createAsyncThunk(
  "analyze/fetchCodeDensity",
  async ({ document_id }: { document_id: string }) => {
    const res = await get<CodeDensityApi>(
      `documents/${encodeURIComponent(document_id)}/code-density`,
      { headers: { Accept: "application/json" } }
    );

    if (res.type !== "success") throw new Error(res.data?.message || "Failed");

    return {
      document_id: String((res.data as any)?.document_id || document_id),
      document_name: String((res.data as any)?.document_name || ""),
      total_code_applications: Number(
        (res.data as any)?.total_code_applications || 0
      ),
      word_count: Number((res.data as any)?.word_count || 0),
      code_density_per_1000_words: Number(
        (res.data as any)?.code_density_per_1000_words || 0
      ),
    };
  }
);

export const fetchDocumentCoverage = createAsyncThunk(
  "analyze/fetchDocumentCoverage",
  async ({ document_id }: { document_id: string }) => {
    const res = await get<DocumentCoverageApi>(
      `documents/${encodeURIComponent(document_id)}/coverage`,
      { headers: { Accept: "application/json" } }
    );

    if (res.type !== "success") throw new Error(res.data?.message || "Failed");

    return {
      document_id: String((res.data as any)?.document_id || document_id),
      word_count: Number((res.data as any)?.word_count || 0),
      highlighted_words: Number((res.data as any)?.highlighted_words || 0),
      coverage_percent: Number((res.data as any)?.coverage_percent || 0),
    };
  }
);

export const fetchCodingRichness = createAsyncThunk(
  "analyze/fetchCodingRichness",
  async (_: void, { getState }) => {
    const s = (getState() as any).analyze as AnalyzeState;
    if (!s.projectId) throw new Error("Missing project id");

    const res = await get<CodingRichnessApi>(
      `projects/${encodeURIComponent(s.projectId)}/coding-richness`,
      { headers: { Accept: "application/json" } }
    );

    if (res.type !== "success") throw new Error(res.data?.message || "Failed");

    const rows = Array.isArray(res.data?.documents) ? res.data.documents : [];
    const maxExclusive =
      rows.reduce(
        (m: number, r: any) =>
          Math.max(m, Number(r.exclusive_codes_count || 0)),
        0
      ) || 1;
    const maxShared =
      rows.reduce(
        (m: number, r: any) => Math.max(m, Number(r.shared_codes_count || 0)),
        0
      ) || 1;

    return { rows, maxExclusive, maxShared };
  }
);

/* ✅ NEW: Highlight distribution (project-level) */
export const fetchHighlightDistribution = createAsyncThunk(
  "analyze/fetchHighlightDistribution",
  async (_: void, { getState }) => {
    const s = (getState() as any).analyze as AnalyzeState;
    if (!s.projectId) throw new Error("Missing project id");

    const res = await get<HighlightDistributionApi>(
      `projects/${encodeURIComponent(s.projectId)}/excerpt-distribution`,
      { headers: { Accept: "application/json" } }
    );

    if (res.type !== "success") throw new Error(res.data?.message || "Failed");

    const dist = Array.isArray((res.data as any)?.distribution)
      ? (res.data as any).distribution
      : [];
    const rows: HighlightDistributionBin[] = dist
      .map((x: any) => ({
        length: Number(x.length || 0),
        count: Number(x.count || 0),
      }))
      .filter((x: any) => x.length > 0);

    return rows;
  }
);

/* ---- Slice ---- */
const analyzeSlice = createSlice({
  name: "analyze",
  initialState,
  reducers: {
    setProjectId(state, action: PayloadAction<string | null>) {
      state.projectId = action.payload;

      state.counts = { internal: 0, external: 0, loading: false, error: null };
      state.codes = { rows: [], loading: false, error: null, max: 1 };
      state.highlights = { rows: [], loading: false, error: null, max: 1 };

      state.codesDescriptor = { items: [], loading: false, error: null };
      state.codesDropdown = { items: [], loading: false, error: null };
      state.descriptorCodes = {
        rows: [],
        max: 1,
        groups: [],
        loading: false,
        error: null,
      };

      state.documentsDropdown = { items: [], loading: false, error: null };

      state.codeDensity = {
        document_id: "",
        document_name: "",
        total_code_applications: 0,
        word_count: 0,
        code_density_per_1000_words: 0,
        loading: false,
        error: null,
      };

      state.codingRichness = {
        rows: [],
        loading: false,
        error: null,
        maxExclusive: 1,
        maxShared: 1,
      };

      state.documentCoverage = {
        document_id: "",
        word_count: 0,
        highlighted_words: 0,
        coverage_percent: 0,
        loading: false,
        error: null,
      };

      state.highlightDistribution = { rows: [], loading: false, error: null };
    },

    setMonth(state, action: PayloadAction<number | undefined>) {
      state.month = action.payload;
    },

    setYear(state, action: PayloadAction<number | undefined>) {
      state.year = action.payload;
    },
  },

  extraReducers: (b) => {
    // counts
    b.addCase(fetchDocCounts.pending, (s) => {
      s.counts.loading = true;
      s.counts.error = null;
    });
    b.addCase(fetchDocCounts.fulfilled, (s, a) => {
      s.counts.loading = false;
      s.counts.internal = a.payload.countInternal ?? 0;
      s.counts.external = a.payload.countExternal ?? 0;
    });
    b.addCase(fetchDocCounts.rejected, (s, a) => {
      s.counts.loading = false;
      s.counts.error = a.error.message || "Failed";
      s.counts.internal = 0;
      s.counts.external = 0;
    });

    // codes
    b.addCase(fetchCodeCounts.pending, (s) => {
      s.codes.loading = true;
      s.codes.error = null;
    });
    b.addCase(fetchCodeCounts.fulfilled, (s, a) => {
      s.codes.loading = false;
      s.codes.rows = a.payload.rows;
      s.codes.max = a.payload.max;
    });
    b.addCase(fetchCodeCounts.rejected, (s, a) => {
      s.codes.loading = false;
      s.codes.error = a.error.message || "Failed";
      s.codes.rows = [];
      s.codes.max = 1;
    });

    // highlights
    b.addCase(fetchHighlightCounts.pending, (s) => {
      s.highlights.loading = true;
      s.highlights.error = null;
    });
    b.addCase(fetchHighlightCounts.fulfilled, (s, a) => {
      s.highlights.loading = false;
      s.highlights.rows = a.payload.rows;
      s.highlights.max = a.payload.max;
    });
    b.addCase(fetchHighlightCounts.rejected, (s, a) => {
      s.highlights.loading = false;
      s.highlights.error = a.error.message || "Failed";
      s.highlights.rows = [];
      s.highlights.max = 1;
    });

    // Codes × Descriptors
    b.addCase(fetchCodesDescriptorCount.pending, (s) => {
      s.codesDescriptor.loading = true;
      s.codesDescriptor.error = null;
      s.codesDescriptor.items = [];
    });
    b.addCase(fetchCodesDescriptorCount.fulfilled, (s, a) => {
      s.codesDescriptor.loading = false;
      s.codesDescriptor.items = a.payload;
    });
    b.addCase(fetchCodesDescriptorCount.rejected, (s, a) => {
      s.codesDescriptor.loading = false;
      s.codesDescriptor.error = a.error.message || "Failed";
      s.codesDescriptor.items = [];
    });

    // /codes -> dropdown
    b.addCase(fetchCodesFlat.pending, (s) => {
      s.codesDropdown.loading = true;
      s.codesDropdown.error = null;
      s.codesDropdown.items = [];
    });
    b.addCase(fetchCodesFlat.fulfilled, (s, a) => {
      s.codesDropdown.loading = false;
      s.codesDropdown.items = a.payload;
    });
    b.addCase(fetchCodesFlat.rejected, (s, a) => {
      s.codesDropdown.loading = false;
      s.codesDropdown.error = a.error.message || "Failed";
    });

    // descriptor-codes-count (groups/flat)
    b.addCase(fetchDescriptorCodesCount.pending, (s) => {
      s.descriptorCodes.loading = true;
      s.descriptorCodes.error = null;
      s.descriptorCodes.rows = [];
      s.descriptorCodes.max = 1;
      s.descriptorCodes.groups = [];
    });
    b.addCase(fetchDescriptorCodesCount.fulfilled, (s, a) => {
      s.descriptorCodes.loading = false;
      s.descriptorCodes.rows = a.payload.rows;
      s.descriptorCodes.max = a.payload.max;
      s.descriptorCodes.groups = a.payload.groups;
    });
    b.addCase(fetchDescriptorCodesCount.rejected, (s, a) => {
      s.descriptorCodes.loading = false;
      s.descriptorCodes.error = a.error.message || "Failed";
    });

    // documents dropdown
    b.addCase(fetchDocumentsDropdown.pending, (s) => {
      s.documentsDropdown.loading = true;
      s.documentsDropdown.error = null;
      s.documentsDropdown.items = [];
    });
    b.addCase(fetchDocumentsDropdown.fulfilled, (s, a) => {
      s.documentsDropdown.loading = false;
      s.documentsDropdown.items = a.payload;
    });
    b.addCase(fetchDocumentsDropdown.rejected, (s, a) => {
      s.documentsDropdown.loading = false;
      s.documentsDropdown.error = a.error.message || "Failed";
      s.documentsDropdown.items = [];
    });

    // code density
    b.addCase(fetchCodeDensity.pending, (s) => {
      s.codeDensity.loading = true;
      s.codeDensity.error = null;
      s.codeDensity.document_id = "";
      s.codeDensity.document_name = "";
      s.codeDensity.total_code_applications = 0;
      s.codeDensity.word_count = 0;
      s.codeDensity.code_density_per_1000_words = 0;
    });
    b.addCase(fetchCodeDensity.fulfilled, (s, a) => {
      s.codeDensity.loading = false;
      s.codeDensity.document_id = a.payload.document_id;
      s.codeDensity.document_name = a.payload.document_name;
      s.codeDensity.total_code_applications = a.payload.total_code_applications;
      s.codeDensity.word_count = a.payload.word_count;
      s.codeDensity.code_density_per_1000_words =
        a.payload.code_density_per_1000_words;
    });
    b.addCase(fetchCodeDensity.rejected, (s, a) => {
      s.codeDensity.loading = false;
      s.codeDensity.error = a.error.message || "Failed";
      s.codeDensity.document_id = "";
      s.codeDensity.document_name = "";
      s.codeDensity.total_code_applications = 0;
      s.codeDensity.word_count = 0;
      s.codeDensity.code_density_per_1000_words = 0;
    });

    // coding richness
    b.addCase(fetchCodingRichness.pending, (s) => {
      s.codingRichness.loading = true;
      s.codingRichness.error = null;
      s.codingRichness.rows = [];
      s.codingRichness.maxExclusive = 1;
      s.codingRichness.maxShared = 1;
    });
    b.addCase(fetchCodingRichness.fulfilled, (s, a) => {
      s.codingRichness.loading = false;
      s.codingRichness.rows = a.payload.rows;
      s.codingRichness.maxExclusive = a.payload.maxExclusive;
      s.codingRichness.maxShared = a.payload.maxShared;
    });
    b.addCase(fetchCodingRichness.rejected, (s, a) => {
      s.codingRichness.loading = false;
      s.codingRichness.error = a.error.message || "Failed";
      s.codingRichness.rows = [];
      s.codingRichness.maxExclusive = 1;
      s.codingRichness.maxShared = 1;
    });

    // coverage
    b.addCase(fetchDocumentCoverage.pending, (s) => {
      s.documentCoverage.loading = true;
      s.documentCoverage.error = null;
      s.documentCoverage.document_id = "";
      s.documentCoverage.word_count = 0;
      s.documentCoverage.highlighted_words = 0;
      s.documentCoverage.coverage_percent = 0;
    });
    b.addCase(fetchDocumentCoverage.fulfilled, (s, a) => {
      s.documentCoverage.loading = false;
      s.documentCoverage.document_id = a.payload.document_id;
      s.documentCoverage.word_count = a.payload.word_count;
      s.documentCoverage.highlighted_words = a.payload.highlighted_words;
      s.documentCoverage.coverage_percent = a.payload.coverage_percent;
    });
    b.addCase(fetchDocumentCoverage.rejected, (s, a) => {
      s.documentCoverage.loading = false;
      s.documentCoverage.error = a.error.message || "Failed";
      s.documentCoverage.document_id = "";
      s.documentCoverage.word_count = 0;
      s.documentCoverage.highlighted_words = 0;
      s.documentCoverage.coverage_percent = 0;
    });

    // ✅ highlight distribution
    b.addCase(fetchHighlightDistribution.pending, (s) => {
      s.highlightDistribution.loading = true;
      s.highlightDistribution.error = null;
      s.highlightDistribution.rows = [];
    });
    b.addCase(fetchHighlightDistribution.fulfilled, (s, a) => {
      s.highlightDistribution.loading = false;
      s.highlightDistribution.rows = a.payload;
    });
    b.addCase(fetchHighlightDistribution.rejected, (s, a) => {
      s.highlightDistribution.loading = false;
      s.highlightDistribution.error = a.error.message || "Failed";
      s.highlightDistribution.rows = [];
    });
  },
});

export const { setProjectId, setMonth, setYear } = analyzeSlice.actions;
export default analyzeSlice.reducer;
